import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { getCommercialCapabilities } from "../pageInside/Rolle";
import { isAuthenticated } from "./index";
import { signinPathFor } from "./postAuthRedirect";
import { elakayAutoText } from "../pageInside/dataElakay";
import { getOwnProviderProduct } from "../dropshippingInterne/services/providerProductService";
import { PROVIDER_ROUTES, providerRedirectState } from "../dropshippingInterne/providerWorkspace";

// Seller workspace guards. Seller My Shop and Seller Product Edit are unchanged for the Seller role
// and for the Combined Sell Center roles (Associate -> Family); an EFFECTIVE Provider role is sent to
// its Provider Center equivalent instead of rendering the Seller page (a direct URL, an old bookmark
// or a shared link all land here). Everyone else keeps the historic /your-step redirect. The backend
// refuses the Seller product endpoints to a Provider independently
// (server_elakay/elakay_middleware/blockProviderFromSellerProducts.js).

const legacySellCenterLink = (search) => {
  const params = new URLSearchParams(search);
  return params.get("tab") === "sell-center" || params.get("sel") === "true";
};

export const SellerMyShopRoute = ({ infoSource, children }) => {
  const location = useLocation();
  const caps = getCommercialCapabilities(infoSource);
  if (caps.canAccessSellerMyShop) return children;
  if (!isAuthenticated()) return <Navigate to={signinPathFor(location)} replace state={{ from: location }} />;
  if (caps.canAccessProviderCenter) {
    return (
      <Navigate
        to={legacySellCenterLink(location.search) ? PROVIDER_ROUTES.dashboard : PROVIDER_ROUTES.myShop}
        replace
        state={providerRedirectState(elakayAutoText("h_pvc_notice_myshop", "Seller My Shop is part of the Seller workspace. Your Provider storefront and tools are here in Provider Center."))}
      />
    );
  }
  return <Navigate to="/your-step" replace state={{ from: location }} />;
};

// A Provider who opens /product/edit/:id: when :id is one of the Provider's OWN Provider Products,
// open the Provider editor for it; otherwise go to My Products. Never renders the Seller editor.
export const ProviderProductEditRedirect = ({ productId }) => {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    let live = true;
    const fallback = {
      to: PROVIDER_ROUTES.products,
      state: providerRedirectState(elakayAutoText("h_pvc_notice_edit", "Seller Product Edit is not available in the Provider role. Manage your products from Provider Center > My Products.")),
    };
    if (!productId) { setTarget(fallback); return undefined; }
    getOwnProviderProduct(isAuthenticated()?.token, productId).then((r) => {
      if (!live) return;
      const own = r && !r.error && r.success !== false && (r.product?._id || r._id);
      setTarget(own
        ? { to: PROVIDER_ROUTES.editProduct(productId), state: providerRedirectState(elakayAutoText("h_pvc_notice_edit_own", "This product is managed from the Provider editor.")) }
        : fallback);
    });
    return () => { live = false; };
  }, [productId]);
  if (!target) {
    return <div className="dsi-skeleton" role="status" aria-busy="true" aria-label={elakayAutoText("h_pvc_redirecting", "Opening Provider Center…")} />;
  }
  return <Navigate to={target.to} replace state={target.state} />;
};

export const SellerProductEditRoute = ({ infoSource, children }) => {
  const location = useLocation();
  const { productId } = useParams();
  const caps = getCommercialCapabilities(infoSource);
  if (caps.canEditSellerProducts) return children;
  if (caps.canAccessProviderCenter) return <ProviderProductEditRedirect productId={productId} />;
  return <Navigate to="/your-step" replace state={{ from: location }} />;
};
